//* Import libraries
import React, { useState, useCallback, useEffect } from "react";

//* Import react-router hooks
import { useNavigate } from "react-router-dom";

//* Import components
import { List } from "../../../component/list/list";
import { Loading } from "../../../component/common/Loading";

//* Import custom hooks
import { useUnits } from "../../../hooks/units/useUnits";

//* Import Auth
import { AuthAPI } from "../../../services/apis/AuthAPI";

//* Import types
type Unit = {
  id: string;
  name: string;
  description?: string;
};

var debounce = require("lodash.debounce");

export function UnitView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();
  const [me, setMe] = useState<any | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [totalUnits, setTotalUnits] = useState<number>(0);

  const {
    units,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteUnit,
    refetch,
    changePage,
    isDeleting,
  } = useUnits(token);

  useEffect(() => {
    async function fetchMe() {
      try {
        const response = await AuthAPI.me(token);
        setMe(response.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
    fetchMe();
  }, [token]);

  useEffect(() => {
    setTotalUnits(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setSearch(value);
      } else {
        setSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (unitToDelete) {
      try {
        const currentPageItems = units.length;
        const currentPage = pagination.currentPage;

        await deleteUnit(unitToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setUnitToDelete(null);
      } catch (error) {
        console.error("Error deleting admin:", error);
      }
    }
  }, [
    unitToDelete,
    deleteUnit,
    units.length,
    pagination.currentPage,
    changePage,
  ]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setUnitToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  if (isDeleting) {
    return <Loading />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="row">
      <List
        search_input_value={searchInputValue}
        search_text="Pesquise pelo nome da unidade"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        title={`Unidades (Total: ${totalUnits})`}
        not_found_message="Nenhum unidade encontrado"
        button_link="/units/create"
        button_text="Adicionar unidade"
        deleting_message="Ao deletar esta unidades, ela não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/units/edit/${id}`)}
        onChangePagination={handleChangePagination}
        onManagerActionClick={(value) => {
          navigate(`/units/view/${value}`, {
            state: { filter: value, key: "unit_id" },
          });
        }}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        items={units.map((unit: Unit) => ({
          ...unit,
          key: unit.id,
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "description", type: "text", header: "Descrição" },
        ]}
        loading_search={isSearching}
      />
    </div>
  );
}
